import IPurchaseRequisitionGridDetail from "models/Requisition/PurchaseRequisitionGridDetailModel";
import { IRequisitionLineAttachment } from "models/Requisition/RequisitionLineAttachmentModel";
import RequisitionPurchaseLineModel, {
	ProjectOrganizationUnitLine,
} from "models/Requisition/RequisitionPurchaseLineModel";
import { BaseService } from "./base.service";
import { IFinancialYear } from "models/FinancialYearDetail";
import {
	IReplenishmentRequestEmail,
	IReplenishmentTaskAssignToUser,
} from "models/Requisition/ReplenishmentRequest";
import { RequisitionLineTransferDetail } from "models/Requisition/RequisitionLineTransfer";
import { UpdateRequisitionLineProcurementMethod } from "models/Requisition/RequisitionProcurementMethodSelection";
import { IFinancialYearForContract } from "models/Requisition/FinancialYearForContract";
import {
	RequisitionCompanyWideLineModel,
	RequisitionPurchaseLineModelByCompanyWide,
} from "models/Requisition/RequisitionPurchaseLineModelByCompanyWide";

class RequisitionLineServiceInternal extends BaseService {
	getRequisitionLineList = async (
		requisitionId: number,
		requisitionStepHistoryId: number,
		isPurchaseOrder: boolean = false,
		purchaseOrderId: number
	): Promise<IPurchaseRequisitionGridDetail[]> =>
		this.callApi<IPurchaseRequisitionGridDetail[]>(
			`RequisitionLine/GetRequisitionLineList/${requisitionId}/${isPurchaseOrder}/${purchaseOrderId}`,
			"GET",
			requisitionStepHistoryId > 0
				? new URLSearchParams({
						requisitionStepHistoryId: requisitionStepHistoryId.toString(),
				  })
				: undefined
		);

	saveRequisitionLineDetail = async (
		model: RequisitionPurchaseLineModel
	): Promise<{
		requisitionLineId: number;
	}> =>
		this.callApi<{
			requisitionLineId: number;
		}>(`RequisitionLine/Save`, "POST", undefined, model);

	getFinancialYearList = async (
		isLast5Years = false
	): Promise<IFinancialYear[]> =>
		this.callApi<IFinancialYear[]>(
			`FinancialYear/GetFinancialYearList`,
			"GET",
			new URLSearchParams({ isLast5Years: isLast5Years.toString() })
		);

	getRequisitionLineDetail = async (
		requisitionLineId: number,
		requisitionStepHistoryId: number,
		isPurchaseOrder: boolean = false,
		purchaseOrderLineId: number
	): Promise<RequisitionPurchaseLineModel> =>
		this.callApi<RequisitionPurchaseLineModel>(
			`RequisitionLine/GetRequisitionLine/${requisitionLineId}/${isPurchaseOrder}/${purchaseOrderLineId}`,
			"GET",
			requisitionStepHistoryId > 0
				? new URLSearchParams({
						requisitionStepHistoryId: requisitionStepHistoryId.toString(),
				  })
				: undefined
		);

	deleteRequisitionLineItem = async (
		requisitionLineId: number,
		companyWideDepartmentId: number | null
	): Promise<undefined> =>
		this.callApi<undefined>(
			`RequisitionLine/Delete/${requisitionLineId}`,
			"DELETE",
			companyWideDepartmentId && companyWideDepartmentId > 0
				? new URLSearchParams({
						companyWideDepartmentId: companyWideDepartmentId.toString(),
				  })
				: undefined
		);

	updateRequisitionLineDetail = async (
		model: RequisitionPurchaseLineModel
	): Promise<number> =>
		this.callApi<number>(`RequisitionLine/Update`, "PUT", undefined, model);

	getRequisitionLineAttachment = async (
		id: number,
		requisitionLineDetailId: number,
		requisitionStepHistoryId: number
	): Promise<IRequisitionLineAttachment[]> => {
		let urlParams = {} as any;
		if (requisitionLineDetailId > 0) {
			urlParams["requisitionLineDetailId"] = requisitionLineDetailId.toString();
		}
		if (requisitionStepHistoryId > 0) {
			urlParams["requisitionStepHistoryId"] =
				requisitionStepHistoryId.toString();
		}
		return this.callApi<IRequisitionLineAttachment[]>(
			`RequisitionLineAttachment/GetAttachmentList/${id}`,
			"GET",
			new URLSearchParams(urlParams)
		);
	};

	saveRequisitionLineAttachment = async (model: FormData): Promise<number> =>
		this.callApi<number>(
			`RequisitionLineAttachment/Save`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	updateRequisitionLineAttachment = async (
		requisitionLineAttachmentId: number,
		model: FormData
	): Promise<{ requisitionLineAttachmentId: number }> =>
		this.callApi<{ requisitionLineAttachmentId: number }>(
			`RequisitionLineAttachment/Update/${requisitionLineAttachmentId}`,
			"PUT",
			undefined,
			model,
			false,
			true
		);

	deleteRequisitionLineAttachment = async (id: number): Promise<undefined> =>
		this.callApi<undefined>(
			`RequisitionLineAttachment/Delete/${id}`,
			"DELETE",
			undefined,
			undefined,
			true
		);

	getStockReplenishmentTaskAssignUserList = async (
		requisitionId: number
	): Promise<IReplenishmentTaskAssignToUser[]> =>
		this.callApi<IReplenishmentTaskAssignToUser[]>(
			`RequisitionLine/GetStockReplenishmentTaskAssignUserList/${requisitionId}`,
			"GET"
		);

	newReplenishmentRequestEmailSend = async (
		model: IReplenishmentRequestEmail
	): Promise<undefined> =>
		this.callApi<undefined>(
			`RequisitionLine/NewReplenishmentRequestEmailSend`,
			"POST",
			undefined,
			model
		);

	getRequisitionLineTransferList = async (
		requisitionId: number
	): Promise<RequisitionLineTransferDetail> =>
		this.callApi<RequisitionLineTransferDetail>(
			`RequisitionLine/GetRequisitionLineTransferList/${requisitionId}`,
			"GET"
		);

	updateRequisitionLineProcurementMethod = async (
		model: UpdateRequisitionLineProcurementMethod[]
	): Promise<undefined> =>
		this.callApi<undefined>(
			`RequisitionLine/UpdateRequisitionLineProcurementMethod`,
			"PUT",
			undefined,
			model
		);

	getFinancialYearsForContract = async (
		startDate: string,
		endDate: string
	): Promise<IFinancialYearForContract> =>
		this.callApi<IFinancialYearForContract>(
			`FinancialYear/GetFinancialYears/${startDate}/${endDate}`,
			"GET"
		);

	saveUpdateCompanyWideLineItem = async (
		model: RequisitionCompanyWideLineModel[]
	): Promise<number> =>
		this.callApi<number>(
			`RequisitionLine/SaveUpdateCompanyWideLineItem`,
			"POST",
			undefined,
			model
		);

	getRequisitionLineDetailByCompanyWide = async (
		requisitionLineId: number,
		requisitionLineHistoryId: number,
		requisitionStepHistoryId: number
	): Promise<RequisitionPurchaseLineModelByCompanyWide> => {
		let urlParams = {} as any;
		if (requisitionLineId && requisitionLineId > 0)
			urlParams["requisitionLineId"] = requisitionLineId.toString();
		if (requisitionLineHistoryId && requisitionLineHistoryId > 0)
			urlParams["requisitionLineHistoryId"] =
				requisitionLineHistoryId.toString();
		if (requisitionStepHistoryId && requisitionStepHistoryId > 0)
			urlParams["requisitionStepHistoryId"] =
				requisitionStepHistoryId.toString();

		return this.callApi<RequisitionPurchaseLineModelByCompanyWide>(
			`RequisitionLine/GetCompanyWideLineItemDetail`,
			"GET",
			Object.keys(urlParams).length ? new URLSearchParams(urlParams) : undefined
		);
	};

	getProjectOrganizationUnitLines = async (
		requisitionId: number
	): Promise<ProjectOrganizationUnitLine[]> =>
		this.callApi<ProjectOrganizationUnitLine[]>(
			`RequisitionLine/GetProjectOrganizationUnitLines/${requisitionId}`,
			"GET"
		);
}

export const RequisitionLineService = new RequisitionLineServiceInternal();
